import React from 'react'
import { NextWrapper } from 'data-fetching-wrapper'
import { initializeApollo } from '@gql/client'
import { PaginaDocument, PaginaNode } from '@gql/graphql'
import StoreLocatorTemplate from '@components/templates/StoreLocatorTemplate/StoreLocatorTemplate'
import { grapheneGet } from '@utils/index'
import { print } from 'graphql'
import {
  DistributoreNode,
  DistributoriStoreLocatorDocument,
  ProvinciaNode,
  RegioneNode,
  RegioniDocument,
  RivenditoreStoreLocatorNode,
  RivenditoriStoreLocatorDocument,
} from '@gql/graphql-get'
import StoreLocatorInternationalTemplate from '@components/templates/StoreLocatorInternationalTemplate/StoreLocatorInternationalTemplate'
import { BRAND } from '@utils/settings'

interface Props {
  pagina?: PaginaNode
  regioni?: RegioneNode[]
  province?: ProvinciaNode[]
  rivenditori?: RivenditoreStoreLocatorNode[]
  rivenditoriFiltered?: RivenditoreStoreLocatorNode[]
  distributori?: DistributoreNode[]
}

const StoreLocator = ({
  pagina,
  regioni = [],
  rivenditori = [],
  province = [],
  rivenditoriFiltered = [],
  distributori = [],
}: Props) => {
  return distributori?.length ? (
    <StoreLocatorInternationalTemplate pagina={pagina} distributori={distributori} />
  ) : (
    <StoreLocatorTemplate
      pagina={pagina}
      regioni={regioni}
      rivenditori={rivenditori}
      province={province}
      rivenditoriFiltered={rivenditoriFiltered}
    />
  )
}

export const getStaticPaths = NextWrapper.getStaticPaths(async ({ locales }) => {
  let paths = []
  for (const locale of locales) {
    if (locale !== 'it-it') continue
    const encodedQuery = encodeURIComponent(print(RegioniDocument))

    const response = await grapheneGet.get(
      `?lang=${locale}&query=${encodedQuery}&operationName=Regioni`
    )
    const { data } = response.data
    data?.regioni?.map((regione) => {
      paths.push([regione.slug])
      // commentato per accorciare i tempi di build e ridurre il carico della macchina durante la pipeline
      // regione?.province?.map((provincia) => {
      //   paths.push([regione.slug, provincia.slug])
      // })
    })
  }

  return {
    paths: paths.map((slug) => ({ params: { slug } })),
    fallback: 'blocking',
  }
}, '/store-locator/[slug]')

export const getStaticProps = NextWrapper.getStaticProps(
  async ({ preview, previewData, locale, params: { slug } }) => {
    const client = initializeApollo(locale)

    const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

    const {
      data: { pagina },
    } = await client.query({
      query: PaginaDocument,
      variables: { chiave: 'store-locator', preview },
      context: {
        headers,
      },
    })

    if (!pagina) {
      return {
        notFound: true,
      }
    }

    if (locale === 'int') {
      const encodedQueryDistributori = encodeURIComponent(print(DistributoriStoreLocatorDocument))

      const responseDistributori = await grapheneGet.get(
        `?lang=${locale}&query=${encodedQueryDistributori}&operationName=DistributoriStoreLocator`
      )

      let dataResponseDistributori = responseDistributori.data

      return {
        props: { pagina, distributori: dataResponseDistributori?.data?.distributoriStoreLocator },
        revalidate: 60 * 20,
      }
    } else {
      let { seo } = pagina

      const zona = {
        regione: (slug && slug[0]) || null,
        provincia: (slug && slug[1]) || null,
      }

      const encodedQuery = encodeURIComponent(print(RegioniDocument))

      const response = await grapheneGet.get(
        `?lang=${locale}&query=${encodedQuery}&operationName=Regioni`
      )
      const { data } = response.data

      const variables = {
        regione: zona?.regione,
        provincia: zona?.provincia,
      }
      const encodedVariablesRivenditori = encodeURIComponent(JSON.stringify(variables))

      const encodedQueryRivenditori = encodeURIComponent(print(RivenditoriStoreLocatorDocument))

      const responseRivenditori = await grapheneGet.get(
        `?lang=${locale}&query=${encodedQueryRivenditori}&operationName=RivenditoriStoreLocator&variables=${encodedVariablesRivenditori}`
      )

      let dataResponseRivenditori = responseRivenditori.data

      const breadcrumbs = [...pagina.breadcrumbs]
      let base = breadcrumbs[1]
      let currentUrl = pagina.breadcrumbs[1].url
      Object.values(zona)
        .filter((item) => item) // escludo i valori null
        .forEach((subPage) => {
          currentUrl += `/${subPage}`
          base = {
            label:
              subPage === 'valle-d-aosta'
                ? "Valle d'Aosta"
                : subPage?.replaceAll('-', ' ')?.capitalize(),
            url: currentUrl,
          }
          breadcrumbs.push(base)
        })

      const url = new URL(pagina?.seo?.canonicalUrl)

      const canonicalUrl = `${url?.protocol}//${url?.hostname}/${locale}${
        breadcrumbs[breadcrumbs.length - 1]?.url
      }/`

      const elemento = breadcrumbs[breadcrumbs.length - 1]?.label

      if (zona.provincia) {
        seo = {
          title: `I nostri rivenditori a ${elemento} | ${BRAND}`,
          description: `Vuoi un prodotto ${BRAND} a ${elemento}? Entra sul sito per scoprire tutti i rivenditori ${BRAND} nelle vicinanze!`,
        }
      } else if (zona.regione) {
        seo = {
          title: `I nostri rivenditori in ${elemento} | ${BRAND}`,
          description: `Vuoi un prodotto ${BRAND} in ${elemento}? Entra sul sito per scoprire tutti i rivenditori ${BRAND} nella regione e visita quello più vicino a te!`,
        }
      }

      return {
        props: {
          pagina: { ...pagina, breadcrumbs, seo: { ...seo, canonicalUrl } },
          regioni: data?.regioni || [],
          province: zona?.regione
            ? data?.regioni.filter((regione) => regione.slug === zona.regione)[0]?.province || []
            : [],
          rivenditori:
            dataResponseRivenditori?.data?.rivenditoriStoreLocator?.rivenditoriStoreLocator || [],
          rivenditoriFiltered:
            dataResponseRivenditori?.data?.rivenditoriStoreLocator?.rivenditoriFiltered || [],
        },
        revalidate: 60 * 20,
      }
    }
  },
  '/store-locator'
)

export default StoreLocator
